/**
 * Was nach einem Versuch passiert: Ergebnis zeigen, persoenlich verbuchen und
 * auf Wunsch nach draussen geben.
 *
 * Das Verbuchen im Geraet geschieht sofort und ohne Nachfrage. Alles, was den
 * Server erreicht - Bestenliste, Duell-Link -, verlangt eine bewusste Handlung,
 * denn damit gibt der Nutzer einen Namen aus der Hand.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  buildPayload,
  challengeUrl,
  createChallenge,
  submitChallengeAttempt,
  submitRun,
  type SubmitResponse,
} from '../api'
import { useDevice } from '../DeviceContext'
import type { RunResult } from '../engine/disciplines'
import { rememberDuel } from '../engine/duels'
import { commitRun, loadProfile, saveProfile } from '../engine/records'

interface ResultPanelProps {
  result: RunResult
  /** Der grosse Wert, z. B. "4,20 m" oder "38,12 s". */
  headline: string
  /** Erlaeuternde Zeile darunter. */
  subline?: string
  /** Wenn gesetzt, wird der Versuch als Antwort auf ein Duell gewertet. */
  challengeCode?: string
  onRetry: () => void
  onChallengeAnswered?: () => void
}

type Status = 'ruht' | 'sendet' | 'gesendet' | 'fehler'

export function ResultPanel({
  result,
  headline,
  subline,
  challengeCode,
  onRetry,
  onChallengeAnswered,
}: ResultPanelProps) {
  const { metrics } = useDevice()
  const [profile, setProfile] = useState(loadProfile)
  const [status, setStatus] = useState<Status>('ruht')
  const [message, setMessage] = useState<string | null>(null)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const [isRecord, setIsRecord] = useState(false)

  // Sofort und einmalig persoenlich verbuchen. Der Schluessel aus Disziplin,
  // Variante und Messwerten verhindert, dass ein erneutes Rendern doppelt zaehlt.
  const commitKey = `${result.discipline}:${result.variant}:${result.distanceCm}:${result.timeMs}`

  useEffect(() => {
    setIsRecord(commitRun(result).isRecord)
    setStatus('ruht')
    setMessage(null)
    setShareLink(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitKey])

  const payload = useMemo(
    () => buildPayload(result, profile.name.trim(), profile.clientId, metrics),
    [result, profile.name, profile.clientId, metrics],
  )

  const nameMissing = profile.name.trim().length < 2
  const busy = status === 'sendet'

  function updateName(name: string): void {
    const next = { ...profile, name }
    setProfile(next)
    saveProfile(next)
  }

  async function handleSubmit(): Promise<void> {
    if (nameMissing) {
      setMessage('Trag kurz einen Namen ein, dann geht es in die Liste.')
      return
    }
    setStatus('sendet')
    setMessage(null)

    const response = challengeCode
      ? await submitChallengeAttempt(challengeCode, payload)
      : await submitRun(payload)

    if (!response.ok) {
      setStatus('fehler')
      setMessage(response.error)
      return
    }

    setStatus('gesendet')
    if (challengeCode) {
      setMessage('Antwort eingetragen.')
      onChallengeAnswered?.()
      return
    }

    const rank = (response.data as SubmitResponse).rank
    setMessage(rank ? `Platz ${rank} in der Bestenliste.` : 'Eingetragen.')
  }

  async function handleChallenge(): Promise<void> {
    if (nameMissing) {
      setMessage('Für ein Duell brauchst du einen Namen.')
      return
    }
    setStatus('sendet')
    setMessage(null)

    const response = await createChallenge(payload)
    if (!response.ok) {
      setStatus('fehler')
      setMessage(response.error)
      return
    }

    // Das Duell in die eigene Merkliste eintragen. Ohne das waere der Code nach
    // dem Teilen verloren - er lebte nur noch in der fremden Chat-App, und der
    // Herausforderer erfuehre nie, ob jemand geantwortet hat.
    rememberDuel({
      code: response.data.code,
      discipline: result.discipline,
      variant: result.variant,
      distanceCm: result.distanceCm,
      timeMs: result.timeMs,
    })

    const url = challengeUrl(response.data.code)
    setShareLink(url)
    setStatus('gesendet')

    const text = `Ich bin ${headline} gescrollt. Schlag das.`
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Scroll-o-Meter', text, url })
        return
      } catch {
        // Teilen-Dialog abgebrochen. Der Link steht trotzdem zum Kopieren da.
      }
    }
    void copyToClipboard(url, setMessage)
  }

  return (
    <div className="result">
      {isRecord && <div className="result__badge">Persönliche Bestleistung</div>}

      <div className="result__value">{headline}</div>
      {subline && <p className="result__subline">{subline}</p>}

      <label className="field">
        <span className="field__label">Dein Name</span>
        <input
          className="field__input"
          type="text"
          value={profile.name}
          maxLength={24}
          placeholder="für Bestenliste und Duell"
          onChange={(event) => updateName(event.target.value)}
        />
      </label>

      <button className="button button--primary button--wide" type="button" onClick={onRetry}>
        Nochmal
      </button>

      <div className="result__actions">
        <button className="button" type="button" disabled={busy} onClick={() => void handleSubmit()}>
          {challengeCode ? 'Antwort abgeben' : 'In die Bestenliste'}
        </button>
        {!challengeCode && (
          <button className="button" type="button" disabled={busy} onClick={() => void handleChallenge()}>
            Duell starten
          </button>
        )}
      </div>

      {message && (
        <p className={status === 'fehler' ? 'message message--error' : 'message message--ok'}>
          {message}
        </p>
      )}

      {shareLink && (
        <div className="share">
          <code className="share__link">{shareLink}</code>
          <button
            className="button button--small"
            type="button"
            onClick={() => void copyToClipboard(shareLink, setMessage)}
          >
            Kopieren
          </button>
        </div>
      )}
    </div>
  )
}

async function copyToClipboard(text: string, notify: (message: string) => void): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    notify('Link kopiert.')
  } catch {
    // Ohne HTTPS oder Nutzergeste verweigert der Browser den Zugriff auf die
    // Zwischenablage. Der Link steht sichtbar da und laesst sich markieren.
    notify('Link konnte nicht kopiert werden – bitte von Hand markieren.')
  }
}
